/**
 * Ticket status and priority as the CRM's five status tones.
 *
 * Both used to be hand-written Tailwind palettes - `bg-violet-100`,
 * `bg-orange-100`, ten colours across two maps - which put six hues on one row
 * and made none of them mean anything. The app already has a vocabulary for a
 * record's standing, and support tickets are not a special case of it.
 *
 * No React and no icons here, so this can be read by anything.
 */
import type { StatusTone } from "../../ui";
import type { TicketPriority, TicketStatus } from "../../../lib/support";

/**
 * `wt` = waiting on somebody, `info` = live and ours, `ok` = settled,
 * `mut` = closed and out of the way.
 *
 * `waiting` is the amber one because it is the only status where the ball is
 * not in our court, and that is the distinction a queue has to make at a
 * glance. `resolved` and `closed` are deliberately different: resolved is a
 * result, closed is an archive, and giving them the same tone would make a
 * quarter of finished work look like it still needs reading.
 */
const STATUS: Record<TicketStatus, StatusTone> = {
  open: "info",
  in_progress: "info",
  waiting: "wt",
  resolved: "ok",
  closed: "mut",
};

/**
 * Only `urgent` gets the refusal tone.
 *
 * A priority column where every row is coloured is a column where nothing is
 * urgent, so `normal` and `low` stay quiet and `high` takes the amber that
 * means "look at this before the quiet ones".
 */
const PRIORITY: Record<TicketPriority, StatusTone> = {
  low: "mut",
  normal: "mut",
  high: "wt",
  urgent: "lt",
};

export function statusTone(s: TicketStatus): StatusTone {
  return STATUS[s] ?? "mut";
}

export function priorityTone(p: TicketPriority): StatusTone {
  return PRIORITY[p] ?? "mut";
}

/** Statuses in queue order: what is live first, what is done last. */
export const STATUSES: TicketStatus[] = ["open", "in_progress", "waiting", "resolved", "closed"];

/** Priorities from quietest to loudest, which is also the order a filter reads. */
export const PRIORITIES: TicketPriority[] = ["low", "normal", "high", "urgent"];

/** Staff id to a display name, falling back to the id so a row is never blank. */
export function staffName(staff: { id: string; name: string }[], id: string | null): string {
  if (!id) return "-";
  return staff.find((s) => s.id === id)?.name ?? id;
}
